import {
  Avatar,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Center,
  Flex,
  Heading,
  IconButton,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, {memo, useEffect, useState} from 'react';
import {MdOutlinePanoramaPhotosphere, MdThumbDown, MdThumbUp} from 'react-icons/md';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import ButtonMinor from '../../components/ButtonMinor';
import {
  photosView,
  pinEditFormSet,
  pinRatingCreateRequest,
  pinRatingDeleteRequest,
  pinRemoveFromFeaturedRequest, pinsByOwnerSet,
} from '../../reducers/pinsReducer';
import {sendAudioClick, sendAudioHover, sendNavTo} from '../../vuplex';
import InAppTooltip from './UnityReactComponents/InAppTooltip';
import PinOptionsMenu from './PinOptionsMenu';
import PermissionBadge from './PermissionBadge';
import PanoIcon from './PanoIcon';
import {GiDrumKit, GiJetpack, GiLyre, GiMagicAxe} from 'react-icons/gi';
import HasPermission from '../../components/HasPermission';
import {devConsole} from '../../hooks/usePermissions';
import {IoMdPhotos} from 'react-icons/io';
import _ from 'lodash';
import PinPhotosModal from './PinPhotosModal';
import ObjectID from 'bson-objectid';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
dayjs.extend(relativeTime);

const PinCard = ({autoLoadPhotos, pin}) => {
  const {
    _id,
    address = 'unknown address',
    dislikes,
    isDislikedByUser,
    isInPrivateFolder,
    isInSharedFolder,
    isLikedByUser,
    isOwnedByUser,
    isUnsavedPinOwned,
    label,
    likes,
    location: {Latitude, Longitude},
    ownerAlias,
    owner,
    ownerIsOnline,
    ownerProfilePhotoURL,
    photos,
    spaceId,
    spaceType,
    streetViewId,
    streetViewIdIsApi2025,
    suggestedLabel,
    zoomLevel,
  } = pin;
  const [isHovering, setIsHovering] = useState(false);
  const [isPinHovering, setIsPinHovering] = useState(false);

  const pinLabel = label === _id ? suggestedLabel : label;
  const inAppPanoId = useSelector(state => state.inApp.panoId);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isCurrentPano = streetViewId === inAppPanoId;
  const unsavedTempPin = useSelector(s => s.pins.pinUnsavedTemp);

  const toast = useToast();

  const spaceIcon = {
    'jetpackCourse': <GiJetpack color="#69A" size="1.25em"/>,
    'drumMachine': <GiDrumKit color="#A69" size="1.25em"/>,
    'harp': <GiLyre color="#A69" size="1.25em"/>,
  }[spaceType];

  const folderOpened = useSelector(s => s.pins.folderOpened);
  const isInFeaturedList = folderOpened && !_.isEmpty(folderOpened.featuredIds);

  const hasPhotos = !_.isEmpty(photos);
  const [photoUrls, setPhotoUrls] = useState([]);

  const loadPhotos = async () => {
    const {payload} = await dispatch(photosView({
      photosS3Keys: photos.map(p => p.s3Key),
    }));

    setPhotoUrls(_.get(payload, 'photoUrls'));
  }

  const handleCardClick = (e) => {
    sendAudioClick(e);

    if (hasPhotos) {
      loadPhotos();
      return;
    }

    sendNavTo({
      lat: Latitude,
      lon: Longitude,
      panoId: streetViewId,
      panoIdIsApi2025: streetViewIdIsApi2025,
      zoom: zoomLevel,
    });
  };


  useEffect(() => {
    if (autoLoadPhotos) loadPhotos();
  }, [autoLoadPhotos]);

  return (
    <Card
      bg={spaceId
        ? isHovering
          ? '#FFDEEE'
          : '#FCE7'
        : hasPhotos
          ? isHovering
            ? '#FFEEDE'
            : '#FEC7'
          : isOwnedByUser
            ? isHovering
              ? '#E5FEE9'
              : '#E5FEE977'
            : isHovering
              ? 'white'
              : 'whiteAlpha.700'
      }
      borderRadius="2xl"
      h="12em"
      overflow="hidden"
      // shadow={isHovering || isPinHovering ? `outline` : null}
      onPointerEnter={(evt) => {
        setIsHovering(true);
        sendAudioHover(evt);
      }}
      onPointerLeave={() => {
        setTimeout(() => setIsHovering(false), 100);
      }}
      fontSize="lg"
    >
      <CardHeader onClick={handleCardClick} p={2}>
        <Flex flex="1" gap="1" alignItems="center" justifyContent="space-between" flexWrap="nowrap">
          <Flex alignItems="center">
            <Avatar
              borderColor={ownerIsOnline ? 'lightgreen' : 'null'}
              name={ownerAlias}
              showBorder
              size="sm"
              src={ownerProfilePhotoURL}
            />

            <InAppTooltip message="View their places">
              <Heading
                marginLeft={1}
                maxW="10em"
                fontWeight="normal"
                size="md"
                noOfLines={1}
                _hover={{textDecoration: 'underline'}}
                onClick={(e) => {
                  sendAudioClick(e);
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(pinsByOwnerSet({
                    ownerAlias,
                    ownerId: owner,
                    ownerProfilePhotoURL,
                  }));
                }}
                onPointerEnter={(e) => {
                  sendAudioHover(e);
                }}
              >
                {ownerAlias}
              </Heading>
            </InAppTooltip>


          </Flex>

          <Flex alignItems="center" gap={1}>
            {spaceIcon}
            {!spaceIcon && hasPhotos && (
              <InAppTooltip message="Click to view photos">
                <IoMdPhotos color="#A96" size="1.5em"/>
              </InAppTooltip>)}
            {!spaceIcon && !hasPhotos && <PanoIcon streetViewId={streetViewId}/>}
            <PermissionBadge isPin isPrivate={isInPrivateFolder} isShared={isInSharedFolder} size="1.5em"/>
            <PinOptionsMenu pin={pin} size="1.5em"/>
          </Flex>

        </Flex>
      </CardHeader>

      <CardBody onClick={handleCardClick} p={1.5} alignItems="center" display="flex" justifyContent="center">
        <InAppTooltip message={pinLabel.length > 54 && pinLabel}>
          <Text fontSize="lg" textAlign="center" noOfLines={2}>
            {isHovering ? address : pinLabel}
          </Text>
        </InAppTooltip>

        {!_id && (
          <Center>
            <ButtonMinor
              isDisabled={isUnsavedPinOwned}
              onClick={() => {
                dispatch(pinEditFormSet(unsavedTempPin));
                navigate('../edit', {relative: 'path'});
              }}
            >
              <MdOutlinePanoramaPhotosphere/>
              &nbsp;
              Save 360
            </ButtonMinor>
          </Center>
        )}

      </CardBody>

      <CardFooter p={0} paddingX={1} marginTop={0} h="3em">

        <Flex alignItems="center" justifyContent="space-between" w="full">
          <Text flexShrink={0} marginLeft={2} noOfLines={1}>{likes} {likes === 1 ? 'like' : 'likes'}</Text>

          <Text color="gray.400">{dayjs(ObjectID(_id).getTimestamp()).fromNow()}</Text>

          <Flex justifyContent="flex-end" gap={0}>

            {isInFeaturedList && (
              <HasPermission permission={devConsole}>
                <IconButton
                  color="red"
                  icon={<GiMagicAxe size="1.5em"/>}
                  onClick={(e) => {
                    sendAudioClick(e);
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(pinRemoveFromFeaturedRequest({pinId: _id}));
                  }}
                  onPointerEnter={(e) => {
                    sendAudioHover(e);
                  }}
                  size="md"
                  variant="buttonDiminished"
                />
              </HasPermission>

            )}

            {/* 👍 */}
            {!isDislikedByUser && !isOwnedByUser && _id && (
              <IconButton
                color={isLikedByUser ? 'wooorldBlue._' : 'grey'}
                icon={<MdThumbUp size="1.5em"/>}
                onClick={async (e) => {
                  sendAudioClick(e);
                  e.preventDefault();
                  e.stopPropagation();

                  const {error} = await dispatch(
                    isLikedByUser
                      ? pinRatingDeleteRequest({
                        pinId: pin._id,
                      })
                      : pinRatingCreateRequest({
                        pinId: pin._id,
                        vote: 1,
                      }));

                  if (error)
                    toast({
                      description: `error`,
                      status: 'error',
                      title: 'Failed to like pin.',
                    });
                  // dispatch(tourReactRequest({promptId, reaction: '👍'}));
                }}
                onPointerEnter={(e) => {
                  sendAudioHover(e);
                }}
                size="md"
                variant="buttonDiminished"
              />
            )}

            {/* 👎 */}
            {!isLikedByUser && !isOwnedByUser && _id && (
              <IconButton
                color={isDislikedByUser ? 'red' : 'grey'}
                icon={<MdThumbDown size="1.5em"/>}
                onClick={async (e) => {
                  sendAudioClick(e);
                  e.preventDefault();
                  e.stopPropagation();

                  const {error} = await dispatch(
                    isDislikedByUser
                      ? pinRatingDeleteRequest({
                        pinId: pin._id,
                      })
                      : pinRatingCreateRequest({
                        pinId: pin._id,
                        vote: -1,
                      }));

                  if (error)
                    toast({
                      description: `error`,
                      status: 'error',
                      title: 'Failed to dislike pin.',
                    });
                  // dispatch(tourReactRequest({promptId, reaction: '👎'}));
                }}
                onPointerEnter={(e) => {
                  sendAudioHover(e);
                }}
                size="md"
                variant="buttonDiminished"
              />
            )}

          </Flex>
        </Flex>

      </CardFooter>

      {!_.isEmpty(photoUrls) && (
        <PinPhotosModal
          onClose={() => {
            setPhotoUrls([]);
          }}
          photoUrls={photoUrls}
          pin={pin}
        />
      )}

    </Card>
  );


};

export default memo(PinCard);
